import EditShiftForm from '@company/components/EditShiftForm/EditShiftForm';
import OfferShiftForm from '@company/components/OfferShiftModal/OfferShiftForm';
import { useUpdateShiftMutation } from '@company/hooks/useShiftMutations';
import * as jobApi from '@company/services/job/api';
import { useJobOptions } from '@company/state/useQueries/useJobOptions';
import useUpdateWorker from '@company/state/useUpdateWorker/useUpdateWorker';
import { EditShiftFormData } from '@company/types/worker-modal.type';
import {
  invalidateWorkerQueries,
  populateEditFormData,
} from '@company/utils/worker-modal-helper';
import { ThLoading } from '@components/elements';
import ConfirmDialog from '@components/elements/ConfirmDialog/ConfirmDialog';
import QueryKeys from '@constants/queryKeys';
import useAlert from '@hooks/useAlert';
import {
  ActionIcon,
  Alert,
  Button,
  Checkbox,
  Divider,
  Group,
  Modal,
  Paper,
  Stack,
  Table,
  Tabs,
  Text,
} from '@mantine/core';
import {
  canCancelSelectedShifts,
  canOfferSelectedShifts,
  filterShiftsByDate,
  getFormattedTime,
  offerShifts,
} from '@services/shift/shiftService';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShiftPosition } from '@th-types/job.detail.type';
import { ShiftStatus } from '@th-types/jobs.type';
import { IWorkerData } from '@th-types/worker.type';
import { IWorkerJobData } from '@th-types/workerProfile.type';
import {
  createDateTimeFromDifferentDates,
  isDateAfterOrNow,
  UTC_TIMEZONE,
} from '@utils/DateUtils';
import { removeDuplicatedShifts } from '@utils/ShiftsUtils';
import { zonedTimeToUtc } from 'date-fns-tz';
import { capitalize } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { BiEdit } from 'react-icons/bi';
import WorkerShiftsHistory from './WorkerShiftsHistory';

interface JobWorkerShiftsProps {
  worker: IWorkerData;
  jobId?: number;
  workerData?: IWorkerJobData;
}

/**
 * Enhanced version of JobWorkerShifts that works without context
 * Used in WorkerModal when context providers are not available
 * Includes detailed shift tables with selection and management capabilities
 */
export default function WorkerShifts({
  worker,
  jobId,
  workerData,
}: JobWorkerShiftsProps) {
  const [activeKey, setActiveKey] = useState<ShiftStatus>(ShiftStatus.OPEN);
  const [selectedShifts, setSelectedShifts] = useState<ShiftPosition[]>([]);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [offerNote, setOfferNote] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedShiftForEdit, setSelectedShiftForEdit] =
    useState<ShiftPosition | null>(null);
  const [editFormData, setEditFormData] = useState<EditShiftFormData>({
    date: null,
    positionId: 0,
    positionName: '',
    wage: 0,
    payRatePeriod: 'hourly',
    startTime: null,
    endTime: null,
    paidBreak: 'yes',
  });
  const { showSuccessAlert, showErrorAlert, alertMessage } = useAlert();

  // Mutation hooks
  const updateShiftMutation = useUpdateShiftMutation();
  const { updateWorker } = useUpdateWorker();
  const queryClient = useQueryClient();

  // Get job options for position select
  const { jobOptions } = useJobOptions();

  // Fetch shifts data using API instead of context
  const {
    data: shiftsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [QueryKeys.COMPANY_JOB_SHIFTS, jobId],
    queryFn: () => jobApi.fetchShiftPositions(jobId!),
    enabled: !!jobId,
  });

  // Extract job timezone for consistent timezone handling
  const jobTimezone = useMemo(() => {
    return (
      workerData?.jobAssignment?.job?.address?.timezoneId ||
      shiftsData?.job?.address?.timezoneId ||
      UTC_TIMEZONE
    );
  }, [
    workerData?.jobAssignment?.job?.address?.timezoneId,
    shiftsData?.job?.address?.timezoneId,
  ]);

  // Offer shifts mutation
  const offerShiftsMutation = useMutation({
    mutationFn: async ({
      shiftIds,
      note,
    }: {
      shiftIds: number[];
      note?: string;
    }) => {
      if (!jobId) throw new Error('Job ID is required');
      return offerShifts(jobId, worker.id, shiftIds, note);
    },
    onSuccess: async (result) => {
      if (result.success && result.statusCode === 200) {
        showSuccessAlert(result.message || 'Shifts offered successfully');
        setSelectedShifts([]);
        setShowOfferModal(false);
        setOfferNote('');
        refetch(); // Update shifts data so offered shifts disappear from Open tab

        // Update worker data to refresh buttons in WorkerInfoSection
        if (jobId) {
          await updateWorker({
            workerId: worker.id,
            jobId,
          });
          invalidateWorkerQueries({
            queryClient,
            workerId: worker.id,
            jobId,
            jobAssignmentId: workerData?.jobAssignment?.id,
          });
        }
      } else {
        showErrorAlert(result.message || 'Failed to offer shifts');
      }
    },
    onError: () => {
      showErrorAlert('Failed to offer shifts');
    },
  });

  // Cancel shifts mutation
  const cancelShiftsMutation = useMutation({
    mutationFn: async (shiftIds: number[]) => {
      if (!jobId) throw new Error('Job ID is required');
      return jobApi.cancelJobShifts(jobId, shiftIds);
    },
    onSuccess: async (result) => {
      if (result.status === 200) {
        showSuccessAlert(result.data.message || 'Shifts canceled successfully');
        setSelectedShifts([]);
        setShowCancelModal(false);
        refetch();

        // Update worker data to refresh buttons in WorkerInfoSection
        if (jobId) {
          await updateWorker({
            workerId: worker.id,
            jobId,
          });
          invalidateWorkerQueries({
            queryClient,
            workerId: worker.id,
            jobId,
            jobAssignmentId: workerData?.jobAssignment?.id,
          });
        }
      } else {
        showErrorAlert(result.data.message || 'Failed to cancel shifts');
      }
    },
    onError: () => {
      showErrorAlert('Failed to cancel shifts');
    },
  });

  // Edit shift handlers
  const handleEditShift = (shift: ShiftPosition) => {
    setSelectedShiftForEdit(shift);
    const formData = populateEditFormData(shift, jobTimezone);
    setEditFormData({
      ...formData,
      payRatePeriod: formData.payRatePeriod as 'hourly' | 'daily',
      paidBreak: formData.paidBreak as 'yes' | 'no',
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedShiftForEdit(null);
    setEditFormData({
      date: null,
      positionId: 0,
      positionName: '',
      wage: 0,
      payRatePeriod: 'hourly',
      startTime: null,
      endTime: null,
      paidBreak: 'yes',
    });
  };

  const handleUpdateShift = () => {
    if (!selectedShiftForEdit || !jobId) {
      return;
    }

    // Validate required fields
    if (
      !editFormData.positionId ||
      editFormData.wage <= 0 ||
      !editFormData.date ||
      !editFormData.startTime ||
      !editFormData.endTime
    ) {
      showErrorAlert('Please fill in all required fields');
      return;
    }

    updateShiftMutation.mutate(
      {
        jobId,
        shiftId: selectedShiftForEdit.id,
        shift: selectedShiftForEdit,
        formData: editFormData,
        jobTimezone,
      },
      {
        onSuccess: async () => {
          // Update the selectedShifts array with the updated shift data
          setSelectedShifts((prevSelectedShifts) =>
            prevSelectedShifts.map((shift) =>
              shift.id === selectedShiftForEdit.id
                ? {
                    ...shift,
                    title: {
                      ...shift.title,
                      id: editFormData.positionId,
                      name: editFormData.positionName,
                    },
                    wage: {
                      ...shift.wage,
                      payRate: editFormData.wage,
                      payRatePeriod: editFormData.payRatePeriod,
                    },
                    breakPaid: editFormData.paidBreak === 'yes',
                    // Update start and end times using proper timezone handling
                    ...(editFormData.date &&
                    editFormData.startTime &&
                    editFormData.endTime
                      ? (() => {
                          // Create dates in the job timezone
                          const startDateTime =
                            createDateTimeFromDifferentDates(
                              editFormData.date!,
                              editFormData.startTime!
                            );
                          const endDateTime = createDateTimeFromDifferentDates(
                            editFormData.date!,
                            editFormData.endTime!
                          );

                          // Convert from job timezone to UTC using zonedTimeToUtc
                          const startUTC = zonedTimeToUtc(
                            startDateTime,
                            jobTimezone
                          );
                          const endUTC = zonedTimeToUtc(
                            endDateTime,
                            jobTimezone
                          );

                          return {
                            start: startUTC.toISOString(),
                            end: endUTC.toISOString(),
                          };
                        })()
                      : {}),
                  }
                : shift
            )
          );
          handleCloseEditModal();
          refetch();

          // Update worker data to refresh buttons in WorkerInfoSection
          if (jobId) {
            await updateWorker({
              workerId: worker.id,
              jobId,
            });
            invalidateWorkerQueries({
              queryClient,
              workerId: worker.id,
              jobId,
              jobAssignmentId: workerData?.jobAssignment?.id,
            });
          }
        },
      }
    );
  };

  const shifts = useMemo(() => {
    // Combine worker-specific shifts with general shifts, avoiding duplicates
    const workerShifts = workerData?.jobAssignment?.shifts || [];
    const generalShifts = shiftsData?.shifts || [];
    if (workerShifts.length > 0) {
      const workerShiftIds = new Set(workerShifts.map((shift) => shift.id));
      const filteredGeneralShifts = generalShifts.filter(
        (shift: ShiftPosition) => !workerShiftIds.has(shift.id)
      );
      return [...workerShifts, ...filteredGeneralShifts];
    }

    return generalShifts;
  }, [shiftsData?.shifts, workerData?.jobAssignment?.shifts]);

  const shiftStatusOptions = [
    { value: ShiftStatus.OPEN, label: 'Open' },
    { value: ShiftStatus.PENDING, label: 'Pending' },
    { value: ShiftStatus.CONFIRMED, label: 'Confirmed' },
  ];

  // Function to get shifts for a specific tab
  const getShiftsForTab = useCallback(
    (tabKey: string) => {
      if (!shifts || shifts.length === 0) {
        return [];
      }

      const filteredShifts = shifts.filter(
        (shift: ShiftPosition) =>
          shift.shiftStatus.toLowerCase() === tabKey.toLowerCase()
      );

      if (tabKey === ShiftStatus.OPEN) {
        // For open shifts, show unassigned shifts available for assignment
        const uniqueShifts = removeDuplicatedShifts(filteredShifts);

        const finalShifts = uniqueShifts.filter((shift: ShiftPosition) => {
          const isAfterNow = isDateAfterOrNow(shift.start, jobTimezone);
          const isUnassigned = !shift.jobAssignment;

          return isAfterNow && isUnassigned;
        });

        return finalShifts;
      }

      // Show shifts assigned to this worker
      const workerShifts = filteredShifts.filter((shift: ShiftPosition) => {
        const workerSpecificShifts = workerData?.jobAssignment?.shifts || [];
        const isFromWorkerData = workerSpecificShifts.some(
          (ws) => ws.id === shift.id
        );

        const isAssignedToWorker =
          shift.jobAssignment?.worker?.id === worker.id || isFromWorkerData;

        return isAssignedToWorker;
      });

      return workerShifts;
    },
    [shifts, worker, jobTimezone, workerData?.jobAssignment?.shifts]
  );

  const handleSelectShift = (shift: ShiftPosition) => {
    setSelectedShifts((prevSelectedShifts) => {
      const isSelected = prevSelectedShifts.some((s) => s.id === shift.id);
      if (isSelected) {
        return prevSelectedShifts.filter((s) => s.id !== shift.id);
      }
      return [...prevSelectedShifts, shift];
    });
  };

  // Action handlers
  const handleOfferShifts = () => {
    if (selectedShifts.length === 0) return;
    setShowOfferModal(true);
  };

  const handleCancelShifts = () => {
    if (selectedShifts.length === 0) return;
    setShowCancelModal(true);
  };

  const confirmOfferShifts = () => {
    const shiftIds = selectedShifts.map((shift) => shift.id);
    offerShiftsMutation.mutate({ shiftIds, note: offerNote || undefined });
  };

  const confirmCancelShifts = () => {
    const shiftIds = selectedShifts.map((shift) => shift.id);
    cancelShiftsMutation.mutate(shiftIds);
  };

  // Check if actions are available
  const canOffer = canOfferSelectedShifts(selectedShifts);
  const canCancel = canCancelSelectedShifts(selectedShifts);

  if (!jobId) {
    return (
      <Paper p="md" withBorder>
        <Text ta="center" c="dimmed">
          No job context available. Shifts data requires a job ID.
        </Text>
      </Paper>
    );
  }

  if (isLoading) {
    return <ThLoading />;
  }

  const renderShiftTable = (date: string, shiftsForDate: ShiftPosition[]) => (
    <Paper key={date} p="sm">
      <Stack gap="sm">
        <Text fw={600} size="sm" c="black">
          {date}
        </Text>
        <Divider />
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              {/* Checkbox column */}
              <Table.Th style={{ width: '40px' }}> </Table.Th>
              <Table.Th>Position</Table.Th>
              <Table.Th ta="center" style={{ width: '200px' }}>
                Scheduled Shift Time
              </Table.Th>
              <Table.Th ta="center" style={{ width: '160px' }}>
                Full/Back Up
              </Table.Th>
              <Table.Th ta="center" style={{ width: '160px' }}>
                Wage
              </Table.Th>
              <Table.Th ta="center" style={{ width: '160px' }}>
                Paid Break
              </Table.Th>
              <Table.Th style={{ width: '40px' }}>Edit</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {shiftsForDate.map((shift, index) => (
              <Table.Tr key={`${shift.id}-${shift.positionIndex || index}`}>
                <Table.Td>
                  <Checkbox
                    checked={selectedShifts.some((s) => s.id === shift.id)}
                    onChange={() => handleSelectShift(shift)}
                    size="sm"
                  />
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{shift.title.name}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" ta="center">
                    {getFormattedTime(shift, jobTimezone)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text
                    fw={500}
                    size="sm"
                    c="black"
                    tt="capitalize"
                    ta="center"
                  >
                    {capitalize(shift.type)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" ta="center">
                    ${shift.wage.payRate}/{capitalize(shift.wage.payRatePeriod)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text
                    fw={500}
                    size="sm"
                    c="black"
                    tt="capitalize"
                    ta="center"
                  >
                    {shift.breakPaid ? 'Yes' : 'No'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <ActionIcon
                    variant="subtle"
                    size="md"
                    c="black"
                    onClick={() => handleEditShift(shift)}
                    style={{ cursor: 'pointer' }}
                  >
                    <BiEdit size={20} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    </Paper>
  );

  return (
    <Stack gap="lg" style={{ flex: 1 }}>
      <Tabs
        value={activeKey}
        onChange={(value) => {
          if (value !== activeKey) {
            setActiveKey(value as ShiftStatus);
            setSelectedShifts([]); // Clear selection when switching tabs
          }
        }}
        style={{ flex: 1 }}
      >
        <Tabs.List>
          {shiftStatusOptions.map((option) => {
            const tabShifts = getShiftsForTab(option.value);
            return (
              <Tabs.Tab
                key={option.value}
                value={option.value}
                disabled={tabShifts.length === 0}
                px="xl"
              >
                {option.label} ({tabShifts.length})
              </Tabs.Tab>
            );
          })}
        </Tabs.List>

        {/* Action buttons for selected shifts */}
        {selectedShifts.length > 0 && (
          <Paper p="md" mt="md" bg="blue.0">
            <Group justify="space-between" align="center">
              <Text size="sm" fw={500}>
                {selectedShifts.length} shift
                {selectedShifts.length !== 1 ? 's' : ''} selected
              </Text>
              <Group gap="sm">
                {activeKey === ShiftStatus.OPEN && canOffer && (
                  <Button
                    size="sm"
                    variant="filled"
                    color="blue"
                    onClick={handleOfferShifts}
                    loading={offerShiftsMutation.isPending}
                  >
                    Offer Shifts
                  </Button>
                )}
                {activeKey !== ShiftStatus.OPEN && canCancel && (
                  <Button
                    size="sm"
                    color="red"
                    onClick={handleCancelShifts}
                    loading={cancelShiftsMutation.isPending}
                  >
                    Cancel Shifts
                  </Button>
                )}
              </Group>
            </Group>
          </Paper>
        )}

        {shiftStatusOptions.map((option) => {
          const tabShifts = getShiftsForTab(option.value);
          const tabGroupedShifts = filterShiftsByDate(tabShifts);

          return (
            <Tabs.Panel
              key={option.value}
              value={option.value}
              px="0"
              pt="lg"
              style={{ flex: 1, overflow: 'visible' }}
            >
              {tabShifts.length > 0 ? (
                <Stack gap="lg" style={{ flex: 1 }}>
                  {tabGroupedShifts.map(({ date, data }) =>
                    renderShiftTable(date, data)
                  )}
                </Stack>
              ) : (
                <Paper p="md" style={{ flex: 1 }}>
                  <Text ta="center" c="dimmed" size="sm">
                    {option.value === ShiftStatus.OPEN
                      ? 'No open shifts available for this job. All future shifts may already be assigned to workers.'
                      : `No ${option.value.toLowerCase()} shifts found for this worker.`}
                  </Text>
                </Paper>
              )}
            </Tabs.Panel>
          );
        })}
      </Tabs>

      {/* Job Shifts History */}
      {workerData?.jobPlacementHistoryList && (
        <WorkerShiftsHistory
          jobPlacementHistoryList={workerData.jobPlacementHistoryList}
          jobTimezone={jobTimezone}
        />
      )}

      {/* Offer Shifts Modal */}
      <Modal
        opened={showOfferModal}
        onClose={() => setShowOfferModal(false)}
        title="Offer Shifts"
        size="75%"
        centered
      >
        <OfferShiftForm
          selectedShifts={selectedShifts}
          worker={worker}
          offerNote={offerNote}
          onOfferNoteChange={setOfferNote}
          onSubmit={confirmOfferShifts}
          onCancel={() => setShowOfferModal(false)}
          isLoading={offerShiftsMutation.isPending}
          onEditShift={handleEditShift}
          jobTimezone={jobTimezone}
        />
      </Modal>

      {/* Cancel Shifts Dialog */}
      <ConfirmDialog
        opened={showCancelModal}
        size="lg"
        onClose={() => setShowCancelModal(false)}
        onConfirm={confirmCancelShifts}
        title="Cancel Shifts"
        message={`Are you sure you want to cancel ${selectedShifts.length} shift${selectedShifts.length !== 1 ? 's' : ''}? This action cannot be undone and the worker will be notified of the cancellation.`}
        loading={cancelShiftsMutation.isPending}
      />

      {/* Edit Shift Modal */}
      {showEditModal && selectedShiftForEdit && (
        <Modal
          opened={showEditModal}
          onClose={handleCloseEditModal}
          title="Update Shift"
          styles={{ title: { fontSize: '1.5rem', fontWeight: 600 } }}
          size="auto"
          centered
        >
          <EditShiftForm
            formData={editFormData}
            onFormDataChange={setEditFormData}
            onSubmit={handleUpdateShift}
            onCancel={handleCloseEditModal}
            isLoading={updateShiftMutation.isPending}
            jobOptions={jobOptions}
          />
        </Modal>
      )}

      {/* Alert Messages */}
      {alertMessage.show && (
        <Alert
          color={alertMessage.variant === 'success' ? 'green' : 'red'}
          variant="light"
          pos="fixed"
          top={20}
          right={20}
          style={{ zIndex: 1000 }}
        >
          {alertMessage.message}
        </Alert>
      )}
    </Stack>
  );
}
